import { Box, Typography } from '@mui/material'
import { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { PageRoute } from 'src/routes/routes-behind-login/page-route'
import { lastStep, lastStepNoLink } from './common-steps'
import { addVisitSearchParam } from 'src/pages/pages-behind-login/port-info-page/port-info-page'

const smallSizeTourBreakpoints: Breakpoint[] = ['sm', 'xs', 'md']

export function constructPortInfoTour({
  breakpoint,
  hasWriteScope,
}: {
  breakpoint: Breakpoint
  hasWriteScope?: boolean
}): Step[] {
  const isSmallSizeTour = smallSizeTourBreakpoints.includes(breakpoint)

  const addVisitSteps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Add a visit
          </Typography>
          <Typography variant="paragraph2" component="p">
            Click “Add Visit” to record a new entry. Choose “Port” for a
            specific berth or terminal visit, or “Country” for general
            information that isn’t tied to one location.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Have the vessel and IMO number, port, terminal and berth names,
            arrival and departure dates, draft measurements and cargo operation
            details ready before you start.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-add-visit',
      data: {
        next: `${PageRoute.PORT_INFO}?${addVisitSearchParam}=true`,
        previous: PageRoute.PORT_INFO,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Type (required) input field
          </Typography>
          <Typography variant="paragraph2">
            Choose the type of information you are recording:
            <ul>
              <li>
                “Port” - when you are recording information about a specific
                berth or terminal visit
              </li>
              <li>
                “Country” - when you are recording general information about a
                country that is not port-specific. Select this if the
                information covers a wider area and isn’t tied to a specific
                port location.
              </li>
            </ul>
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-type-field',
      placement: 'right',
      data: {
        deferredTarget: true,
        previous: PageRoute.PORT_INFO,
        next: PageRoute.PORT_INFO,
      },
    },
  ]

  const defaultPortInfoTour: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            What is Port Info
          </Typography>
          <Typography variant="paragraph2" component="p">
            Port Info is a knowledge-sharing service that collects and organises
            information from past visits around the world.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            When a vessel visits a port, crew and operators can record valuable
            details about the experience - from technical specifications like
            draft requirements and manifold pressure to practical notes about
            local authorities, berthing procedure, and available services.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            We made this tour to get you onboarded. You can skip the tour at any
            point, and always start it again.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Searching and filtering
          </Typography>
          <Typography variant="paragraph2" component="p">
            The filter bar narrows down which past visits you see.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Note: filters apply to the Ports section only. The Countries section
            holds general information rather than visit-specific data, so it is
            not affected.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-filter-bar',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Filter by recency
          </Typography>
          <Typography variant="paragraph2">
            Use the “Last visit” dropdown to only show ports visited within a
            given timeframe, for example the last 30 days.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-last-visit-filter',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Country, port, terminal and berth
          </Typography>
          <Typography variant="paragraph2">
            These filters are hierarchical:
            <ul>
              <li>
                Selecting a country limits the port filter to ports in that
                country
              </li>
              <li>Changing a higher-level filter resets the ones below it</li>
              <li>Each filter accepts multiple values</li>
            </ul>
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-location-filters',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Start over
          </Typography>
          <Typography variant="paragraph2">
            “Reset Filters” clears all active filters at once.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-reset-filters',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Navigation panel
          </Typography>
          <Typography variant="paragraph2">
            The left panel organises all port information into expandable
            sections.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-nav-panel',
      placement: 'right',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Countries section
          </Typography>
          <Typography variant="paragraph2">
            Country-level entries hold general information that isn’t tied to a
            specific port. Click a country to expand it and see its entries.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-countries-section',
      placement: 'right',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Ports section
          </Typography>
          <Typography variant="paragraph2">
            Port visit records are organised as Port → Terminal → Berth, with
            the port code and country shown alongside the port name.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-ports-section',
      placement: 'right',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Try it out
          </Typography>
          <Typography variant="paragraph2" component="p">
            Click a port to expand it and see its terminals, then click a
            terminal to load its berth details on the right.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Click “Next” when you are done.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-nav-panel',
      placement: 'right',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Visit details
          </Typography>
          <Typography variant="paragraph2" component="p">
            Once you select a terminal, this panel lists its berths with the
            date each was last visited. Expand a berth to see visit information,
            voyage details, draft information, cargo operations, port services
            and general remarks.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Berths visited more than once show “Latest Visit” and “History”
            tabs. You can also print a record, or edit it if you have write
            access.
          </Typography>
        </>
      ),
      target: '.tour-step-port-info-detail-panel',
      placement: 'left',
    },
    ...(hasWriteScope ? addVisitSteps : []),
    hasWriteScope
      ? {
          ...lastStepNoLink,
          data: {
            previous: `${PageRoute.PORT_INFO}?${addVisitSearchParam}=true`,
          },
        }
      : lastStepNoLink,
  ]

  if (!isSmallSizeTour)
    return defaultPortInfoTour.map((step) => ({
      ...step,
      hideCloseButton: true,
      disableCloseOnEsc: true,
    }))

  return defaultPortInfoTour.map((step) => ({
    ...step,
    target: 'body',
    placement: 'center',
    hideCloseButton: true,
    disableCloseOnEsc: true,
  }))
}
